/* PhotoCropModal — crop + zoom any just-picked image before upload.
 *
 * Opens with a File, shows a fixed square viewport (circular mask for
 * avatars), lets the user drag to reposition, zoom via slider / wheel /
 * pinch, then exports the visible crop through a canvas as WebP
 * (JPEG fallback) and hands the Blob back to the caller — the caller owns
 * the actual upload. Zoom 1x is "cover fit": the image can never be
 * dragged or zoomed to leave a gap inside the crop window.
 *
 * No dependency: ~200 lines of pointer math beats shipping a cropper lib
 * for one viewport (frontend rules: no new deps without strong reason).
 * EXIF orientation: the preview <img> and canvas drawImage both honor
 * EXIF in the evergreen browsers this internal tool targets.
 */
import { useEffect, useRef, useState } from 'react'

/** Fetch an already-uploaded (same-origin) image back into a File so the
 *  crop modal can re-edit it — used by "click the photo to adjust". */
export async function fileFromImageUrl(url: string, baseName = 'photo'): Promise<File> {
  const resp = await fetch(url, { credentials: 'include' })
  if (!resp.ok) throw new Error(`Could not load the current photo (HTTP ${resp.status})`)
  const blob = await resp.blob()
  if (!blob.type.startsWith('image/')) throw new Error('The stored file is not an image')
  const ext = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : blob.type.includes('gif') ? 'gif' : 'jpg'
  return new File([blob], `${baseName}.${ext}`, { type: blob.type })
}

const VIEW = 320 // CSS px of the square crop viewport
const ZOOM_MIN = 1
const ZOOM_MAX = 4

export type PhotoCropAspect = 'square' | 'wide'

export default function PhotoCropModal({
  file,
  title = 'Adjust photo',
  round = true,
  aspect = 'square',
  allowAspectToggle = false,
  outputSize = 512,
  onCancel,
  onSave,
}: {
  file: File
  title?: string
  round?: boolean
  /** The initial crop shape. Existing callers default to the profile-photo square. */
  aspect?: PhotoCropAspect
  /** Enables the Circle / Rectangle shape switch used by organization logos. */
  allowAspectToggle?: boolean
  outputSize?: number
  onCancel: () => void
  /** Receives the cropped image and selected aspect; caller uploads it. Throw to keep the modal open with an error. */
  onSave: (blob: Blob, suggestedName: string, aspect: PhotoCropAspect) => Promise<void> | void
}) {
  const [url] = useState(() => URL.createObjectURL(file))
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null)
  const [cropAspect, setCropAspect] = useState<PhotoCropAspect>(aspect)
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  // Active pointers for drag + two-finger pinch (pointerId → position)
  const pointersRef = useRef(new Map<number, { x: number; y: number }>())
  const gestureRef = useRef<{ panStart: { x: number; y: number }; origin: { x: number; y: number }; pinchDist: number; pinchZoom: number } | null>(null)
  // Latest zoom/dims for the native wheel listener (bound once, reads fresh)
  const zoomLatestRef = useRef(zoom)
  zoomLatestRef.current = zoom
  const dimsLatestRef = useRef(dims)
  dimsLatestRef.current = dims

  useEffect(() => () => URL.revokeObjectURL(url), [url])

  // Escape cancels (unless mid-save)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onCancel() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel, saving])

  const cropWidth = VIEW
  const cropHeight = cropAspect === 'wide' ? VIEW / 3 : VIEW
  const coverScale = dims ? Math.max(cropWidth / dims.w, cropHeight / dims.h) : 1
  const scale = coverScale * zoom

  // The image must always cover the viewport — clamp pan to the overhang.
  const clampPan = (p: { x: number; y: number }, z = zoom) => {
    if (!dims) return p
    const s = coverScale * z
    const maxX = Math.max(0, (dims.w * s - cropWidth) / 2)
    const maxY = Math.max(0, (dims.h * s - cropHeight) / 2)
    return { x: Math.min(maxX, Math.max(-maxX, p.x)), y: Math.min(maxY, Math.max(-maxY, p.y)) }
  }

  const applyZoom = (next: number) => {
    const z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next))
    setZoom(z)
    setPan(prev => clampPan(prev, z))
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if (saving) return
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    const pts = [...pointersRef.current.values()]
    if (pts.length === 2) {
      gestureRef.current = {
        panStart: pan, origin: pts[0],
        pinchDist: Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y),
        pinchZoom: zoom,
      }
    } else {
      gestureRef.current = { panStart: pan, origin: { x: e.clientX, y: e.clientY }, pinchDist: 0, pinchZoom: zoom }
    }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    const g = gestureRef.current
    if (!g || !pointersRef.current.has(e.pointerId)) return
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    const pts = [...pointersRef.current.values()]
    if (pts.length >= 2 && g.pinchDist > 0) {
      const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y)
      applyZoom(g.pinchZoom * (dist / g.pinchDist))
    } else if (pts.length === 1) {
      setPan(clampPan({ x: g.panStart.x + (e.clientX - g.origin.x), y: g.panStart.y + (e.clientY - g.origin.y) }))
    }
  }
  const onPointerUp = (e: React.PointerEvent) => {
    pointersRef.current.delete(e.pointerId)
    if (pointersRef.current.size === 0) gestureRef.current = null
    else {
      // Pinch ended with one finger still down — restart as a plain drag.
      const remaining = [...pointersRef.current.values()][0]
      gestureRef.current = { panStart: pan, origin: remaining, pinchDist: 0, pinchZoom: zoom }
    }
  }
  // Wheel zoom needs a NATIVE non-passive listener: React binds `wheel`
  // passively at the root, so a JSX onWheel can't preventDefault and the
  // page behind the modal would scroll while zooming.
  useEffect(() => {
    const el = viewportRef.current
    if (!el) return
    const onWheelNative = (e: WheelEvent) => {
      e.preventDefault()
      const d = dimsLatestRef.current
      if (!d) return
      const z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoomLatestRef.current * (1 - e.deltaY * 0.0015)))
      setZoom(z)
      setPan(prev => {
        const currentCropHeight = cropAspect === 'wide' ? VIEW / 3 : VIEW
        const s = Math.max(VIEW / d.w, currentCropHeight / d.h) * z
        const maxX = Math.max(0, (d.w * s - VIEW) / 2)
        const maxY = Math.max(0, (d.h * s - currentCropHeight) / 2)
        return { x: Math.min(maxX, Math.max(-maxX, prev.x)), y: Math.min(maxY, Math.max(-maxY, prev.y)) }
      })
    }
    el.addEventListener('wheel', onWheelNative, { passive: false })
    return () => el.removeEventListener('wheel', onWheelNative)
  }, [cropAspect])

  const save = async () => {
    const img = imgRef.current
    if (!img || !dims || saving) return
    setSaving(true)
    setError(null)
    try {
      // Source crop: the viewport mapped back into natural-image pixels.
      const srcWidth = cropWidth / scale
      const srcHeight = cropHeight / scale
      const sx = dims.w / 2 - pan.x / scale - srcWidth / 2
      const sy = dims.h / 2 - pan.y / scale - srcHeight / 2
      const canvas = document.createElement('canvas')
      canvas.width = cropAspect === 'wide' ? outputSize * 3 : outputSize
      canvas.height = outputSize
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('Canvas unavailable in this browser')
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(img, sx, sy, srcWidth, srcHeight, 0, 0, canvas.width, canvas.height)
      const blob = await new Promise<Blob | null>(res => canvas.toBlob(res, 'image/webp', 0.92))
      const finalBlob = blob ?? await new Promise<Blob | null>(res => canvas.toBlob(res, 'image/jpeg', 0.92))
      if (!finalBlob) throw new Error('Could not encode the cropped image')
      await onSave(finalBlob, finalBlob.type === 'image/webp' ? 'photo.webp' : 'photo.jpg', cropAspect)
    } catch (err: any) {
      setError(err?.message || 'Could not save the photo')
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay photo-crop-overlay" onClick={() => { if (!saving) onCancel() }}>
      <div className="modal-card photo-crop-card" onClick={e => e.stopPropagation()}>
        <div className="photo-crop-header">
          <h3>{title}</h3>
          <button type="button" className="modal-close" aria-label="Close" disabled={saving} onClick={onCancel}>&times;</button>
        </div>
        <div className="photo-crop-hint">Drag to reposition · scroll or pinch to zoom</div>
        {allowAspectToggle && (
          <div className="photo-crop-shape-row" role="group" aria-label="Logo shape">
            <span className="photo-crop-shape-label">Shape</span>
            <button
              type="button"
              className={`photo-crop-shape-btn${cropAspect === 'square' ? ' active' : ''}`}
              aria-pressed={cropAspect === 'square'}
              disabled={saving}
              onClick={() => { setCropAspect('square'); setZoom(1); setPan({ x: 0, y: 0 }) }}
            >
              <span aria-hidden="true">◯</span> Circle
            </button>
            <button
              type="button"
              className={`photo-crop-shape-btn${cropAspect === 'wide' ? ' active' : ''}`}
              aria-pressed={cropAspect === 'wide'}
              disabled={saving}
              onClick={() => { setCropAspect('wide'); setZoom(1); setPan({ x: 0, y: 0 }) }}
            >
              <span aria-hidden="true">▭</span> Rectangle
            </button>
          </div>
        )}
        <div
          ref={viewportRef}
          className="photo-crop-viewport"
          style={{ width: cropWidth, height: cropHeight, touchAction: 'none' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {/* eslint-disable-next-line jsx-a11y/alt-text */}
          <img
            ref={imgRef}
            src={url}
            draggable={false}
            onLoad={e => setDims({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
            style={{
              position: 'absolute', left: '50%', top: '50%',
              width: dims ? dims.w : undefined,
              maxWidth: 'none',
              transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
              transformOrigin: 'center',
              willChange: 'transform',
              opacity: dims ? 1 : 0,
            }}
          />
          <div className={`photo-crop-mask${cropAspect === 'wide' ? ' wide' : (round ? ' round' : '')}`} aria-hidden />
        </div>
        <div className="photo-crop-zoom-row">
          <button type="button" className="photo-crop-zoom-btn" aria-label="Zoom out" disabled={saving || zoom <= ZOOM_MIN} onClick={() => applyZoom(zoom - 0.25)}>−</button>
          <input
            type="range"
            min={ZOOM_MIN}
            max={ZOOM_MAX}
            step={0.01}
            value={zoom}
            disabled={saving}
            aria-label="Zoom"
            onChange={e => applyZoom(parseFloat(e.target.value))}
          />
          <button type="button" className="photo-crop-zoom-btn" aria-label="Zoom in" disabled={saving || zoom >= ZOOM_MAX} onClick={() => applyZoom(zoom + 0.25)}>+</button>
        </div>
        {error && <div className="photo-crop-error">{error}</div>}
        <div className="photo-crop-actions">
          <button type="button" className="photo-crop-btn ghost" disabled={saving} onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }) }}>Reset</button>
          <div style={{ flex: 1 }} />
          <button type="button" className="photo-crop-btn ghost" disabled={saving} onClick={onCancel}>Cancel</button>
          <button type="button" className="photo-crop-btn primary" disabled={saving || !dims} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save photo'}
          </button>
        </div>
      </div>
    </div>
  )
}
